/**
 * Siembra de trazas por lotes.
 *
 * El endpoint histórico es `GET /api/positions/batch?imeis=…`, y tiene dos
 * techos que una flota grande revienta:
 *
 *   1. Los IMEIs viajan en la query string. Un IMEI son 15 dígitos + coma, así
 *      que 1.000 vehículos son ~16 KB de URL — muy por encima del límite
 *      práctico de proxies y del `large_client_header_buffers` de nginx.
 *   2. El backend rechaza más de 50 IMEIs por request. Con 1.000 vehículos eso
 *      son 20 requests, y el código llamaba una sola vez: la respuesta era un
 *      400 y la flota arrancaba sin trails.
 *
 * Este módulo trocea la flota y la pide por `POST /api/positions/batch`, que
 * lleva los IMEIs en el body. Los chunks salen con un límite de concurrencia:
 * el objetivo es no abrir 20 conexiones simultáneas contra un backend que
 * puede estar en cold start (Fly), no exprimir la latencia.
 *
 * Degradación: si el cliente de API no expone el POST (mocks de test) o el
 * backend responde 404/405 (deploy rotativo con el backend viejo todavía
 * arriba), se cae al GET troceado a 50 por request. El resultado es idéntico,
 * solo más lento.
 */

import { api as defaultApi } from "../../api/client.js";

/** IMEIs por request en el camino POST. El backend acepta hasta 500. */
export const POST_CHUNK_SIZE = 200;
/** IMEIs por request en el camino GET (tope duro del endpoint histórico). */
export const GET_CHUNK_SIZE = 50;
/** Requests simultáneos. Más que esto solo apila cola en el backend. */
export const BATCH_CONCURRENCY = 4;

/** Trocea `items` en arrays de a lo sumo `size`. */
export function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/** ¿Este error significa "el backend no conoce el POST"? */
function isEndpointMissing(err) {
  return err?.status === 404 || err?.status === 405;
}

/**
 * Ejecuta `worker` sobre cada elemento con un tope de tareas en vuelo.
 * Devuelve los resultados en el orden de entrada.
 */
async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = new Array(Math.min(limit, items.length)).fill(null).map(
    async () => {
      for (;;) {
        const index = cursor;
        cursor += 1;
        if (index >= items.length) return;
        results[index] = await worker(items[index], index);
      }
    },
  );
  await Promise.all(runners);
  return results;
}

/**
 * Últimas `limit` posiciones de cada IMEI, en un solo objeto
 * `{ [imei]: [{lat, lng, heading, speed, timestamp}, …] }` (oldest→newest,
 * igual que el endpoint).
 *
 * Un chunk que falle NO tumba al resto: se registra y se sigue. Las trazas son
 * un seed — lo que falte lo rellena la WS con las posiciones que vayan
 * llegando —, así que media flota con trail es estrictamente mejor que
 * ninguna. La única excepción es un fallo del PRIMER chunk por endpoint
 * ausente, que reintenta todo por GET.
 *
 * @param {string[]} imeis
 * @param {number} limit  filas crudas por IMEI
 * @param {object} [options]
 * @param {object} [options.api]          cliente de API (inyectable en tests)
 * @param {number} [options.concurrency]
 * @param {AbortSignal} [options.signal]  cancela los chunks pendientes (los ya
 *                                        abortados se registran como fallo de
 *                                        chunk y devuelven parcial)
 * @returns {Promise<Record<string, Array>>}
 */
export async function fetchPositionsBatched(imeis, limit, options = {}) {
  const {
    api = defaultApi,
    concurrency = BATCH_CONCURRENCY,
    signal = null,
  } = options;

  const unique = [...new Set(imeis.filter(Boolean))];
  if (unique.length === 0) return {};

  const canPost = typeof api.postPositionsBatch === "function";

  if (canPost) {
    const chunks = chunk(unique, POST_CHUNK_SIZE);
    let endpointMissing = false;
    const parts = await mapWithConcurrency(chunks, concurrency, async (ids) => {
      if (endpointMissing || signal?.aborted) return null;
      try {
        return await api.postPositionsBatch(ids, limit, { signal });
      } catch (err) {
        if (isEndpointMissing(err)) {
          // Backend viejo: abandonar el camino POST entero, no solo este chunk.
          endpointMissing = true;
          return null;
        }
        console.warn(
          `[positionsBatch] chunk de ${ids.length} IMEIs falló:`,
          err?.message ?? err,
        );
        return null;
      }
    });
    if (!endpointMissing) return merge(parts);
    console.warn(
      "[positionsBatch] POST /api/positions/batch no disponible — usando GET",
    );
  }

  const chunks = chunk(unique, GET_CHUNK_SIZE);
  const parts = await mapWithConcurrency(chunks, concurrency, async (ids) => {
    if (signal?.aborted) return null;
    try {
      return await api.getPositionsBatch(ids, limit, { signal });
    } catch (err) {
      console.warn(
        `[positionsBatch] chunk de ${ids.length} IMEIs falló:`,
        err?.message ?? err,
      );
      return null;
    }
  });
  return merge(parts);
}

function merge(parts) {
  const out = {};
  for (const part of parts) {
    if (!part) continue;
    for (const imei of Object.keys(part)) out[imei] = part[imei];
  }
  return out;
}

export default fetchPositionsBatched;
